import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { motion, useAnimation } from 'motion/react';

interface PullToRefreshProps {
  onRefresh: () => Promise<void>;
  children: ReactNode;
}

export function PullToRefresh({ onRefresh, children }: PullToRefreshProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isPulling, setIsPulling] = useState(false);
  const [startY, setStartY] = useState(0);
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  const MAX_PULL_DISTANCE = 80;
  const THRESHOLD = 60;
  const controls = useAnimation();

  const handleTouchStart = (e: React.TouchEvent | React.MouseEvent) => {
    // Only engage if at the top of the scroll container
    if (window.scrollY > 0) return;
    
    // Check if the scrollable container is at the top
    const scrollContainer = document.querySelector('main');
    if (scrollContainer && scrollContainer.scrollTop > 0) return;

    setIsPulling(true);
    setStartY('touches' in e ? e.touches[0].clientY : e.clientY);
  };

  const handleTouchMove = (e: React.TouchEvent | React.MouseEvent) => {
    if (!isPulling || isRefreshing) return;

    const currentY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    const distance = currentY - startY;

    if (distance > 0) {
      // Prevent default scroll when pulling down
      if (distance > 10) {
         // This can help prevent the browser's native pull-to-refresh
         // e.preventDefault(); might cause issues with passive listeners, so handled via touch-action in CSS
      }
      
      // Add resistance
      const pull = Math.min(distance * 0.5, MAX_PULL_DISTANCE);
      setPullDistance(pull);
      controls.set({ y: pull });
    }
  };

  const handleTouchEnd = async () => {
    if (!isPulling) return;
    setIsPulling(false);

    if (pullDistance >= THRESHOLD && !isRefreshing) {
      setIsRefreshing(true);
      controls.start({ y: 50, transition: { type: 'spring', stiffness: 300, damping: 20 } });
      
      try {
        await onRefresh();
      } finally {
        setIsRefreshing(false);
        setPullDistance(0);
        controls.start({ y: 0, transition: { type: 'spring', stiffness: 300, damping: 20 } });
      }
    } else {
      setPullDistance(0);
      controls.start({ y: 0, transition: { type: 'spring', stiffness: 300, damping: 20 } });
    }
  };

  return (
    <div 
      ref={containerRef}
      className="relative flex-1 flex flex-col w-full"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchEnd}
      onMouseDown={handleTouchStart}
      onMouseMove={handleTouchMove}
      onMouseUp={handleTouchEnd}
      onMouseLeave={handleTouchEnd}
    >
      <motion.div 
        className="absolute w-full flex justify-center z-[100] left-0 pointer-events-none"
        style={{ 
          top: -40, 
          opacity: (!isPulling && !isRefreshing && pullDistance === 0) ? 0 : 1,
          visibility: (!isPulling && !isRefreshing && pullDistance === 0) ? 'hidden' : 'visible'
        }}
        animate={controls}
      >
        <div className="bg-white dark:bg-gray-800 rounded-full shadow-md p-2 flex items-center justify-center border border-gray-100 dark:border-gray-700 pointer-events-none">
          <RefreshCw 
            size={20} 
            className={`text-blue-500 ${isRefreshing ? 'animate-spin' : ''}`}
            style={{ 
              transform: `rotate(${Math.min(pullDistance * 3, 180)}deg)`,
              transition: isRefreshing ? 'none' : 'transform 0.1s ease-out' 
            }} 
          />
        </div>
      </motion.div>
      
      <motion.div animate={controls} className="flex-1 flex flex-col w-full">
        {children}
      </motion.div>
    </div>
  );
}
